import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { normalizeProviderRole } from '@lyrahealth-inc/shared-app-logic/src/features/providers/providerRole'
import { EpisodeParticipantRole } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import type { CareHistoryProvider } from '@lyrahealth-inc/shared-app-logic/src/models/providers/CareHistoryProviders'
import {
  CareServiceProgram,
  CareServiceProvider,
  CareServicesSidePanelData,
} from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/clientDetailsSidePanel/careServices/types'

import { useCareTeamData } from './useCareTeamData'
import { useConcurrentCareData } from './useConcurrentCareData'
import { getAuthUserId } from '../../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../../data/lyraTherapy/clientSelectors'
import { getProviderDetailsData } from '../../../../../providers/data/providerSelectors'
import { useGetConcurrentCareQuery } from '../../../../episodes/data/concurrentCareApi'
import { useSelectedEpisodeForClient } from '../../../../episodes/data/hooks/useSelectedEpisodeForClient'
import { useClientAppointmentsData } from '../../data/hooks/useClientAppointmentsData'

export const useCareServicesSidePanelData = (
  careHistoryProviders?: CareHistoryProvider[],
  { skip, onManageTeamPress }: { skip?: boolean; onManageTeamPress?: () => void } = {},
): CareServicesSidePanelData | undefined => {
  const intl = useIntl()
  const authProviderId = useSelector(getAuthUserId)
  const clientId = useSelector(getSelectedClientId)
  const providerDetailsData = useSelector(getProviderDetailsData)
  const { data: selectedEpisode } = useSelectedEpisodeForClient()
  const concurrentCareEpisodes = useConcurrentCareData()
  const careTeam = useCareTeamData({ skip })
  const { data: allAppointments = [] } = useClientAppointmentsData()

  // `useConcurrentCareData` drops the selected episode, since its consumers present the client's *other*
  // care. The raw response still carries an entry for it, which is the only place the episode's primary
  // provider is named when the logged-in provider is a secondary on it.
  const { data: rawConcurrentCare = [] } = useGetConcurrentCareQuery({ patientId: clientId }, { skip: !clientId })

  const secondaryProviderParticipants = useMemo(
    () =>
      selectedEpisode?.participants?.filter(
        (p) => p.role === EpisodeParticipantRole.SECONDARY_PROVIDER && p.status === 'active',
      ) ?? [],
    [selectedEpisode?.participants],
  )

  return useMemo(() => {
    const programs: CareServiceProgram[] = []

    if (selectedEpisode) {
      const programName =
        getProgramDisplayLabel(intl, selectedEpisode.program_name, 'clientProfile') || selectedEpisode.program_name

      const episodeAppointments = allAppointments.filter((a) => a.episodeId === selectedEpisode.id)

      // DBT Teens providers can be a secondary on an episode they do not own (see getEpisodesForDetailsPanel),
      // so the primary provider is the logged-in provider only when the episode is theirs. Otherwise the
      // concurrent care entry for this episode names the owner.
      const isEpisodeOwner = selectedEpisode.provider_id === authProviderId
      const loggedInProviderName =
        providerDetailsData?.full_name ||
        [providerDetailsData?.first_name, providerDetailsData?.last_name].filter(Boolean).join(' ')
      const selectedEpisodeConcurrentCare = rawConcurrentCare.find((item) => item.episodeId === selectedEpisode.id)
      const owner = selectedEpisodeConcurrentCare?.provider
      const loggedInProviderAppointment = episodeAppointments.find(
        (appointment) =>
          appointment.lyraProviderId === providerDetailsData?.lyra_id || appointment.lyraProviderId === authProviderId,
      )
      const loggedInProviderImageUrl =
        loggedInProviderAppointment?.image?.imageUrl ??
        loggedInProviderAppointment?.provider?.lyraProviderImage?.imageUrl

      let primaryProvider: CareServiceProvider | undefined
      if (isEpisodeOwner && loggedInProviderName) {
        primaryProvider = {
          providerId: authProviderId,
          providerName: loggedInProviderName,
          providerRole: normalizeProviderRole(providerDetailsData?.lyra_type),
          providerImageUrl: owner?.imageUrl ?? loggedInProviderImageUrl,
        }
      } else if (!isEpisodeOwner && owner?.name) {
        primaryProvider = {
          providerId: owner.id,
          providerName: owner.name,
          providerRole: normalizeProviderRole(owner.lyra_type),
          providerImageUrl: owner.imageUrl ?? undefined,
        }
      }

      // The logged-in provider is resolved from their own details rather than the episode roster, so they are
      // listed once here whether they are the episode's primary or one of its secondaries.
      const isLoggedInProviderSecondary =
        !isEpisodeOwner &&
        Boolean(loggedInProviderName) &&
        secondaryProviderParticipants.some((p) => p.participant_lyra_id === providerDetailsData?.lyra_id)
      const loggedInSecondaryProvider: CareServiceProvider | undefined = isLoggedInProviderSecondary
        ? {
            providerId: authProviderId,
            providerName: loggedInProviderName,
            providerRole: normalizeProviderRole(providerDetailsData?.lyra_type),
            providerImageUrl: loggedInProviderImageUrl,
          }
        : undefined

      const otherSecondaryProviders: CareServiceProvider[] = secondaryProviderParticipants
        .filter((participant) => participant.participant_lyra_id !== providerDetailsData?.lyra_id)
        .map((participant) => {
          const appointmentBySecondary = episodeAppointments.find(
            (a) => a.lyraProviderId === participant.participant_lyra_id,
          )

          if (appointmentBySecondary?.provider) {
            const provider = appointmentBySecondary.provider
            return {
              providerId: participant.participant_lyra_id,
              providerName:
                provider.display_name || [provider.first_name, provider.last_name].filter(Boolean).join(' '),
              providerRole:
                normalizeProviderRole(appointmentBySecondary.programTaxonomy?.role) ??
                normalizeProviderRole(provider.lyra_type),
              providerImageUrl: appointmentBySecondary.image?.imageUrl ?? provider.lyraProviderImage?.imageUrl,
            }
          }

          return { providerName: '' }
        })
        .filter((p) => p.providerName)

      const providers = [primaryProvider, loggedInSecondaryProvider, ...otherSecondaryProviders].filter(
        (provider): provider is CareServiceProvider => provider !== undefined,
      )

      if (programName && (providers.length > 0 || careTeam)) {
        programs.push({ programName, providers, careTeam: careTeam && { ...careTeam, onManageTeamPress } })
      }
    }

    if (concurrentCareEpisodes.length > 0) {
      for (const episode of concurrentCareEpisodes) {
        if (!episode.programName) continue
        programs.push({
          programName: episode.programName,
          providers: [
            {
              providerId: episode.providerId,
              providerName: episode.providerName,
              providerRole: normalizeProviderRole(episode.providerLyraType),
              providerImageUrl: episode.providerImageUrl,
            },
          ],
        })
      }
    }

    if (programs.length === 0) return undefined

    const canMessage = (providerId?: string) =>
      Boolean(
        providerId &&
        providerId !== authProviderId &&
        careHistoryProviders?.some(
          (candidate) => candidate.participant_lyra_id === providerId && candidate.can_message,
        ),
      )

    return {
      programs: programs.map((program) => ({
        ...program,
        providers: program.providers.map((provider) => ({
          ...provider,
          canMessage: canMessage(provider.providerId),
        })),
        careTeam: program.careTeam && {
          ...program.careTeam,
          members: program.careTeam.members.map((member) => ({
            ...member,
            canMessage: canMessage(member.providerId),
          })),
        },
      })),
    }
  }, [
    selectedEpisode,
    allAppointments,
    rawConcurrentCare,
    authProviderId,
    providerDetailsData,
    secondaryProviderParticipants,
    concurrentCareEpisodes,
    careTeam,
    onManageTeamPress,
    careHistoryProviders,
    intl,
  ])
}
